import { Site } from '@envato/foundation-types'
import * as classnames from 'classnames'
import * as React from 'react'
import * as titleize from 'titleize'

import { config } from '../../../common/config'
import Sanitizer from '../../../common/utils/sanitizer'
import BreadCrumb from '../../components/breadcrumb'
import EmptyResultsPlaceholder from '../../components/emptyResultsPlaceholder'
import HorizontalMenu from '../../components/horizontalMenu'
import ItemsGrid from '../../components/itemsGrid'
import LoadingSpinnerPlaceholder from '../../components/loadingSpinnerPlaceholder'
import PageContentSection from '../../components/pageContentSection'
import PageTextHeading from '../../components/pageTextHeading'
import SearchPageContentWrapper from '../../components/searchPageContentWrapper'
import { LinkTab, Tabs } from '../../components/tabs'
import ThumbnailsSidebar from '../../components/thumbnailsSidebar'
import UsDollarsNoticeContainer from '../../components/usDollarsNotice/usDollarsNoticeContainer'
import VideoControlsContainer from '../../components/video/videoControlsContainer'

import * as styles from './topSellersPage.module.css'

import { Props } from '../../containers/topSellersPageContainer'

export const buildBreadcumbLinks = (category?: string) => {
  if (!category) {
    return [{ name: 'Home', path: '/' }, { name: 'Files' }]
  } else {
    const categoryArray = category.split('/')
    return [
      { name: 'Home', path: '/' },
      ...categoryArray.map((fragement, index) => {
        const pathName = category.substring(
          0,
          category.indexOf(categoryArray[index]) + categoryArray[index].length
        )
        return {
          name: titleize(fragement.replace('-', ' ')),
          path: '/popular_item/by_category?category=' + pathName
        }
      })
    ]
  }
}

const TopSellersPage = (props: Props) => {
  const {
    items,
    featuredAuthors,
    location,
    experimentsReady,
    currentSite,
    isLoading,
    metadata,
    subcategories,
    currentCategoryName
  } = props
  const hasItems = items && items.length > 0

  const category = new Sanitizer(location.query.category)
    .length({ max: 100 })
    // eslint-disable-next-line
    .replace(/[^\w\/-]/gi)
    .asString()

  const period = new Sanitizer(location.query.period, 'week').asValidString([
    'week',
    'month'
  ])

  const defaultAvatarImgSrc = `${
    config.assets.PUBLIC_PATH
  }/images/default-user.jpg`
  const featuredAuthorsThumbnails = featuredAuthors.map(author => ({
    imgSrc: author.profile_image_url || defaultAvatarImgSrc,
    link: author.profile_path,
    title: author.username
  }))
  const shouldShowVideoControls = currentSite === Site.videohive

  const tabsValue = `?period=${period}`
  const title = metadata ? metadata.heading : 'Top Sellers'

  const className = classnames({
    [styles.withCategory]: !!currentCategoryName,
    [styles.withSubcategories]: !!subcategories
  })

  return (
    <article className={className}>
      {(isLoading || hasItems || metadata) && (
        <div className={styles.breadcrumbArea}>
          <BreadCrumb menus={buildBreadcumbLinks(category)} />
        </div>
      )}
      <PageTextHeading
        title={title}
        subtitle={metadata && metadata.sub_heading}
      />
      <SearchPageContentWrapper experimentsReady={experimentsReady}>
        <PageContentSection withShadow>
          <div className={styles.navbar}>
            {currentCategoryName && (
              <div className={styles.categoryName}>
                More Best Selling: <b>{currentCategoryName}:</b>
              </div>
            )}
            {subcategories && (
              <HorizontalMenu>
                {subcategories.map(catg => (
                  <HorizontalMenu.MenuItem
                    to={`?category=${catg.path}`}
                    key={catg.path}
                  >
                    {catg.name}
                  </HorizontalMenu.MenuItem>
                ))}
              </HorizontalMenu>
            )}
            {!subcategories && !category && (
              <Tabs value={tabsValue}>
                <LinkTab to="?period=week">This week</LinkTab>
                <LinkTab to="?period=month">Last month</LinkTab>
              </Tabs>
            )}
            <div className={styles.controls}>
              <UsDollarsNoticeContainer />
              {shouldShowVideoControls && <VideoControlsContainer />}
            </div>
          </div>
          {isLoading && <LoadingSpinnerPlaceholder />}
          {hasItems && (
            <div className={styles.itemsGrid}>
              <ItemsGrid
                items={items}
                location={location}
                listName="Top Sellers: Top Selling Items"
              />
              <div className={styles.sidebar}>
                <ThumbnailsSidebar
                  title="Featured Authors"
                  thumbnails={featuredAuthorsThumbnails}
                />
              </div>
            </div>
          )}
          {!isLoading && !hasItems && (
            <EmptyResultsPlaceholder
              heading="Nothing exists here"
              subHeading="We couldn't find any data for this page. Please try again later."
            />
          )}
        </PageContentSection>
      </SearchPageContentWrapper>
    </article>
  )
}

export default TopSellersPage
